/* eslint-disable @alice-health/w-helper */
import { WTag } from '@alice-health/wonderland-react'
import Image from 'next/image'
import { forwardRef } from 'react'
import { RouteLink } from '@/modules/accredited-establishments/components/RouteLink'
import { AddressLines } from '@/modules/accredited-establishments/functions'
import { RouteDestination } from '@/modules/accredited-establishments/types/RouteDestination'

/* O handoff desenha a imagem em 48px, sem variacao por breakpoint. */
const IMAGE_SIZE = 48

interface EstablishmentsListItemProps {
  title: string
  address?: AddressLines
  image: string
  tags?: string[]
  referencedId: string
  destination?: RouteDestination
  onClick?: (referencedId: string) => void
}

const EstablishmentsListItem = forwardRef<
  HTMLButtonElement,
  EstablishmentsListItemProps
>(function EstablishmentsListItem(
  { title, address, image, tags, referencedId, destination, onClick },
  ref
) {
  return (
    /*
     * No hover o handoff troca o fundo para `gl-color-off-gray-00` e tira a
     * borda. A borda vira da cor do fundo em vez de desaparecer: remover a
     * borda de 1px mudaria a altura do cartao no hover.
     *
     * A caixa do cartao fica nesta `div` e nao no botao porque o link de rota
     * e irmao do botao: o modelo de conteudo do HTML proibe ancora dentro de
     * `button`.
     */
    <div className='group flex flex-col w-full h-full p-gl-spacing-06 bg-sys-color-surface-background-light border border-gl-color-transparent-black-10 rounded-gl-border-radius-lg hover:bg-gl-color-off-gray-00 hover:border-gl-color-off-gray-00'>
      {/* eslint-disable-next-line @alice-health/w-button */}
      <button
        ref={ref}
        className='flex flex-col items-start gap-gl-spacing-08 w-full text-left'
        onClick={() => onClick && onClick(referencedId)}
        aria-label={`Abrir detalhes de ${title}`}
      >
        <div className='flex items-center gap-gl-spacing-04 w-full'>
          {/*
           * `size-[48px]` repete os 48px por CSS: o preflight do Tailwind poe
           * `height: auto` em `img` e ignora o atributo, e foto em retrato
           * ficava mais alta. Em px porque a raiz do app usa font-size 14px e
           * `size-12` daria 42px. `object-top` preserva o rosto no recorte.
           */}
          <Image
            className='shrink-0 size-[48px] object-cover object-top rounded-gl-border-radius-sm'
            src={image}
            alt='Imagem representando um estabelecimento credenciado'
            height={IMAGE_SIZE}
            width={IMAGE_SIZE}
          />

          {/*
           * `min-w-0`: sem isso o item flex adota a largura do conteudo e o
           * `truncate` dos filhos nunca dispara.
           */}
          <div className='flex flex-col min-w-0 flex-1'>
            <span className='truncate text-gl-font-size-md font-gl-font-weight-semibold text-sys-color-content-primary'>
              {title}
            </span>

            {address ? (
              <span className='line-clamp-2 text-gl-font-size-sm font-gl-font-weight-regular leading-gl-line-height-rule-02 text-sys-color-content-tertiary'>
                {address.street}
                <br />
                {address.locality}
              </span>
            ) : null}
          </div>
        </div>

        {tags?.length ? (
          <div className='tag-pill-theme flex items-center gap-gl-spacing-01'>
            {tags.map(tag => (
              <WTag
                key={tag}
                label={tag}
                color='gray'
                size='small'
              />
            ))}
          </div>
        ) : null}
      </button>

      {destination ? (
        <RouteLink
          className='mt-gl-spacing-04 self-start'
          destination={destination}
        />
      ) : null}
    </div>
  )
})

export { EstablishmentsListItem }
export type { EstablishmentsListItemProps }
